import apiClient from '@/api/apiClient';
import type { PaginatedResponse } from '@/types/api';
import type { MonitoringListParams } from '@/api/modules/monitoringApi';

const systemAdminMonitoringApi = {
  async getTemplates(params: MonitoringListParams = {}): Promise<PaginatedResponse<any>> {
    try {
      return await apiClient.get('/system-admin/monitoring-templates/', { params });
    } catch (error) {
      console.error('Get Monitoring Templates:', error);
      throw error;
    }
  },

  async getTemplate(id: string): Promise<any> {
    try {
      return await apiClient.get(`/system-admin/monitoring-templates/${id}`);
    } catch (error) {
      console.error('Get Monitoring Template:', error);
      throw error;
    }
  },

  async createTemplate(data: {
    versioned_group_name: string;
    is_template: boolean;
    is_active: boolean;
    entity_type: string[];
    entity_relationship_scopes?: string[];
    checks: any[];
    should_promote?: boolean;
  }): Promise<any> {
    try {
      return await apiClient.post('/system-admin/monitoring-templates/', data);
    } catch (error) {
      console.error('Create Monitoring Template:', error);
      throw error;
    }
  },

  async updateTemplate(id: string, data: { versioned_group_name: string }): Promise<any> {
    try {
      return await apiClient.put(`/system-admin/monitoring-templates/${id}`, data);
    } catch (error) {
      console.error('Update Monitoring Template:', error);
      throw error;
    }
  },

  async updateInstance(templateId: string, instanceId: string, data: any): Promise<any> {
    try {
      return await apiClient.put(
        `/system-admin/monitoring-templates/${templateId}/instances/${instanceId}`,
        data,
      );
    } catch (error) {
      console.error('Update Monitoring Template Instance:', error);
      throw error;
    }
  },

  async createDraft(templateId: string, baseInstanceId?: string): Promise<any> {
    try {
      return await apiClient.post(`/system-admin/monitoring-templates/${templateId}/draft`, {
        base_instance_id: baseInstanceId ?? null,
      });
    } catch (error) {
      console.error('Create Monitoring Template Draft:', error);
      throw error;
    }
  },

  async discardDraft(templateId: string): Promise<any> {
    try {
      return await apiClient.delete(`/system-admin/monitoring-templates/${templateId}/draft`);
    } catch (error) {
      console.error('Discard Monitoring Template Draft:', error);
      throw error;
    }
  },

  async promoteToProduction(templateId: string, instanceId: string): Promise<any> {
    try {
      return await apiClient.post(
        `/system-admin/monitoring-templates/${templateId}/instances/${instanceId}/promote`,
        {},
      );
    } catch (error) {
      console.error('Promote Monitoring Template Instance:', error);
      throw error;
    }
  },

  async toggleActive(templateId: string, isActive: boolean): Promise<any> {
    try {
      return await apiClient.post(`/system-admin/monitoring-templates/${templateId}/toggle-active`, {
        is_active: isActive,
      });
    } catch (error) {
      console.error('Toggle Monitoring Template Active:', error);
      throw error;
    }
  },

  async deleteTemplate(templateId: string): Promise<any> {
    try {
      return await apiClient.delete(`/system-admin/monitoring-templates/${templateId}`);
    } catch (error) {
      console.error('Delete Monitoring Template:', error);
      throw error;
    }
  },
};

export default systemAdminMonitoringApi;
